import { message, notification } from 'ant-design-vue';
import dayjs from 'dayjs';

/**
 * 将 File 对象转换为 base64 字符串
 * @param file File 对象
 * @returns Promise<string> 包含 base64 字符串的 Promise
 */
const getBase64 = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * 获取当前页面的tab列表
 * @param pageKey 页面key
 * @returns tab列表
 */
const getTabsByPage = (pageKey: string) => {
  const { menuList } = useMenuStore();
  const page = menuList.find((item) => item.key === pageKey);
  if (page && page.tabs && page.tabs.length) {
    return page.tabs;
  }
  return [];
};

/**
 * 格式化数字，保留指定小数位数（默认2位），并添加千分位分隔符
 * @param num 要格式化的数字
 * @param decimalPlaces 小数位数（可选，默认2位）
 * @returns 格式化后的字符串
 */
const formatNumber = (num, decimalPlaces = 2) => {
  // 处理非数字情况
  if (isNaN(num) || num === null || num === undefined) return '-';

  // Intl.NumberFormat 配置
  const options: any = {
    style: 'decimal', // 十进制格式
    useGrouping: true, // 启用千分位分隔
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };

  // 可选：指定小数位数（如 2 位、0 位）
  if (decimalPlaces !== undefined) {
    options.minimumFractionDigits = decimalPlaces;
    options.maximumFractionDigits = decimalPlaces;
  }

  // 中文环境（分隔符为逗号，小数位为点），可改为 'en-US' 等其他语言
  return new Intl.NumberFormat('zh-CN', options).format(num);
};

/**
 * 根据字符串获取颜色类名
 * @param str 输入字符串
 * @returns 对应的颜色类名
 */
const getColorByStr = (str: string) => {
  const colorClasses = [
    'bg-[#1E88E5]/[0.08] text-[#1E88E5]',
    'bg-[#1976D2]/[0.08] text-[#1976D2]',
    'bg-[#1565C0]/[0.08] text-[#1565C0]',
    'bg-[#42A5F5]/[0.08] text-[#42A5F5]',
    'bg-[#64B5F6]/[0.08] text-[#64B5F6]',
    'bg-[#00ACC1]/[0.08] text-[#00ACC1]',
    'bg-[#0097A7]/[0.08] text-[#0097A7]',
    'bg-[#00838F]/[0.08] text-[#00838F]',
    'bg-[#26C6DA]/[0.08] text-[#26C6DA]',
    'bg-[#4DD0E1]/[0.08] text-[#4DD0E1]',
    'bg-[#43A047]/[0.08] text-[#43A047]',
    'bg-[#388E3C]/[0.08] text-[#388E3C]',
    'bg-[#2E7D32]/[0.08] text-[#2E7D32]',
    'bg-[#66BB6A]/[0.08] text-[#66BB6A]',
    'bg-[#81C784]/[0.08] text-[#81C784]',
    'bg-[#FB8C00]/[0.08] text-[#FB8C00]',
    'bg-[#F57C00]/[0.08] text-[#F57C00]',
    'bg-[#EF6C00]/[0.08] text-[#EF6C00]',
    'bg-[#FFA726]/[0.08] text-[#FFA726]',
    'bg-[#FFB74D]/[0.08] text-[#FFB74D]',
    'bg-[#E53935]/[0.08] text-[#E53935]',
    'bg-[#D32F2F]/[0.08] text-[#D32F2F]',
    'bg-[#C62828]/[0.08] text-[#C62828]',
    'bg-[#EF5350]/[0.08] text-[#EF5350]',
    'bg-[#E57373]/[0.08] text-[#E57373]',
    'bg-[#8E24AA]/[0.08] text-[#8E24AA]',
    'bg-[#7B1FA2]/[0.08] text-[#7B1FA2]',
    'bg-[#6A1B9A]/[0.08] text-[#6A1B9A]',
    'bg-[#AB47BC]/[0.08] text-[#AB47BC]',
    'bg-[#BA68C8]/[0.08] text-[#BA68C8]',
    'bg-[#FBC02D]/[0.08] text-[#FBC02D]',
    'bg-[#F9A825]/[0.08] text-[#F9A825]',
    'bg-[#F57F17]/[0.08] text-[#F57F17]',
    'bg-[#FFD54F]/[0.08] text-[#FFD54F]',
    'bg-[#FFE082]/[0.08] text-[#FFE082]',
    'bg-[#00796B]/[0.08] text-[#00796B]',
    'bg-[#00695C]/[0.08] text-[#00695C]',
    'bg-[#004D40]/[0.08] text-[#004D40]',
    'bg-[#26A69A]/[0.08] text-[#26A69A]',
    'bg-[#4DB6AC]/[0.08] text-[#4DB6AC]',
    'bg-[#37474F]/[0.08] text-[#37474F]',
    'bg-[#78909C]/[0.08] text-[#78909C]',
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colorClasses[Math.abs(hash) % colorClasses.length];
};

/**
 * 复制文本到剪贴板
 * @param text 要复制的文本
 * @returns Promise<void>
 */
const copyText = async (text: string) => {
  try {
    if (text) {
      // 检查 Clipboard API 是否可用
      if (!navigator.clipboard) {
        // 降级方案：使用旧的 document.execCommand
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);

          if (successful) {
            message.success('复制成功');
          } else {
            message.error('复制失败，请手动复制');
          }
        } catch (err) {
          document.body.removeChild(textArea);
          message.error('复制失败，请手动复制');
        }
        return;
      }

      // 使用 Clipboard API
      await navigator.clipboard.writeText(text);
      message.success('复制成功');
    }
  } catch (error: any) {
    console.error('复制失败:', error);
    message.error('复制失败，请手动复制');
  }
};

/**
 * 下载图片数组
 * @param imageUrls 图片URL数组
 * @param options 下载选项
 * @returns Promise<void>
 */
const downloadImages = async (
  imageUrls: string[],
  options: {
    filenamePrefix?: string;
    delay?: number;
    onProgress?: (current: number, total: number) => void;
    onError?: (url: string, error: any) => void;
  } = {},
): Promise<void> => {
  const { filenamePrefix = 'image', delay = 100, onProgress, onError } = options;
  if (!imageUrls || imageUrls.length === 0) {
    notification.warning({
      message: '图片URL数组为空',
      description: '请添加要下载的图片URL',
    });
    return;
  }
  try {
    let downloadedCount = 0;
    for (let i = 0; i < imageUrls.length; i++) {
      const url = imageUrls[i];
      try {
        // 创建下载链接
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        // 获取图片数据
        const blob = await response.blob();
        // 创建下载链接
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        // 生成文件名
        const timestamp = new Date().getTime();
        const extension = url.split('.').pop()?.split('?')[0] || 'jpg';
        const filename = `${filenamePrefix}_${timestamp}_${i + 1}.${extension}`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // 清理URL对象
        window.URL.revokeObjectURL(downloadUrl);
        downloadedCount++;
        // 进度回调
        if (onProgress) {
          onProgress(i + 1, imageUrls.length);
        }
        // 延迟，避免下载过快
        if (i < imageUrls.length - 1 && delay > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      } catch (error) {
        console.error(`下载图片失败: ${url}`, error);
        // 错误回调
        if (onError) {
          onError(url, error);
        }
        // 继续下载其他图片
        continue;
      }
    }
    notification.info({
      message: `图片下载完成`,
      description: `成功下载 ${downloadedCount}/${imageUrls.length} 张图片`,
    });
  } catch (error) {
    console.error('批量下载图片失败:', error);
    notification.error({
      message: '批量下载图片失败',
      description: '请检查网络连接或联系管理员',
    });
    throw error;
  }
};

/**
 * 批量下载视频
 * @param videoUrls 视频URL数组
 * @param options 配置选项
 * @returns Promise
 */
const downloadVideos = async (
  videoUrls: string[],
  options: {
    filenamePrefix?: string; // 文件名前缀
    delay?: number; // 下载间隔时间(毫秒)
    onProgress?: (current: number, total: number, url: string) => void; // 整体进度回调
    onItemProgress?: (progress: number, url: string) => void; // 单个视频下载进度
    onError?: (error: any, url: string) => void; // 错误回调
  } = {},
): Promise<void> => {
  const { filenamePrefix = 'video', delay = 500, onProgress, onItemProgress, onError } = options;

  if (!videoUrls || videoUrls.length === 0) {
    notification.warning({
      message: '视频URL列表不能为空',
      description: '请添加要下载的视频URL',
    });
    return;
  }

  const total = videoUrls.length;
  let completed = 0;

  for (let i = 0; i < videoUrls.length; i++) {
    const videoUrl = videoUrls[i];

    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', videoUrl, true);
        xhr.responseType = 'blob';

        // 监听单个视频下载进度
        if (onItemProgress) {
          xhr.onprogress = (event) => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100;
              onItemProgress(Math.round(percentComplete), videoUrl);
            }
          };
        }

        // 下载完成处理
        xhr.onload = () => {
          if (xhr.status === 200) {
            const blob = xhr.response;
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            // 生成文件名
            const extension = videoUrl.split('.').pop()?.split('?')[0] || 'mp4';
            const filename = `${filenamePrefix}_${i + 1}.${extension}`;

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // 清理URL对象
            window.URL.revokeObjectURL(downloadUrl);

            completed++;
            if (onProgress) {
              onProgress(completed, total, videoUrl);
            }

            resolve();
          } else {
            reject(new Error(`HTTP error! status: ${xhr.status}`));
          }
        };

        // 下载错误处理
        xhr.onerror = () => {
          const error = new Error('视频下载失败');
          reject(error);
        };

        // 开始下载
        xhr.send();
      });

      // 添加延迟,避免同时下载过多
      if (i < videoUrls.length - 1 && delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.error(`下载视频失败 (${videoUrl}):`, error);

      if (onError) {
        onError(error, videoUrl);
      } else {
        notification.error({
          message: `视频 ${i + 1} 下载失败`,
          description: '请检查网络连接或联系管理员',
        });
      }
    }
  }
  notification.info({
    message: `视频下载完成`,
    description: `成功下载 ${completed}/${total} 个视频`,
  });
};

/**
 * 下载文件
 * @param fileUrl 文件URL
 * @param filename 文件名(可选,默认为 'file')
 * @returns Promise<void>
 */
const downloadFile = async (fileUrl: string, filename: string = 'file'): Promise<void> => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(downloadUrl);
    notification.info({
      message: `文件下载完成`,
      description: `文件 ${filename} 下载成功`,
    });
    return Promise.resolve();
  } catch (error) {
    console.error(`下载文件失败 (${fileUrl}):`, error);
    notification.error({
      message: `文件 ${filename} 下载失败`,
      description: '请检查网络连接或联系管理员',
    });
    throw error;
  }
};

/**
 * 将 base64 字符串转换为 File 对象
 * @param base64String base64 字符串(支持带 data:image/png;base64, 前缀或纯 base64)
 * @param filename 文件名(可选,默认为 'file')
 * @param mimeType 文件 MIME 类型(可选,会尝试从 base64 前缀自动识别)
 * @returns File 对象
 */
const base64ToFile = (base64String: string, filename: string = 'file', mimeType?: string): File => {
  // 处理 data URL 格式
  let base64Data = base64String;
  let detectedMimeType = mimeType;

  // 如果包含 data URL 前缀,提取 MIME 类型和 base64 数据
  const dataUrlPattern = /^data:([^;]+);base64,(.+)$/;
  const matches = base64String.match(dataUrlPattern);

  if (matches) {
    detectedMimeType = detectedMimeType || matches[1];
    base64Data = matches[2];
  }

  // 如果没有指定 MIME 类型,默认使用 application/octet-stream
  const finalMimeType = detectedMimeType || 'application/octet-stream';

  // 将 base64 解码为二进制数据
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // 创建 Blob 对象
  const blob = new Blob([bytes], { type: finalMimeType });

  // 如果文件名没有扩展名,根据 MIME 类型添加
  let finalFilename = filename;
  if (!filename.includes('.')) {
    const extension = getExtensionFromMimeType(finalMimeType);
    finalFilename = `${filename}${extension}`;
  }

  // 将 Blob 转换为 File
  return new File([blob], finalFilename, { type: finalMimeType });
};

/**
 * 根据 MIME 类型获取文件扩展名
 * @param mimeType MIME 类型
 * @returns 文件扩展名(带点)
 */
const getExtensionFromMimeType = (mimeType: string): string => {
  const mimeMap: Record<string, string> = {
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'application/pdf': '.pdf',
    'text/plain': '.txt',
    'text/html': '.html',
    'application/json': '.json',
    'video/mp4': '.mp4',
    'video/webm': '.webm',
    'audio/mpeg': '.mp3',
    'audio/wav': '.wav',
  };

  return mimeMap[mimeType] || '';
};

/**
 * 格式化手机号，保留前3位和后4位，中间用星号替换
 * @param phone 要格式化的手机号
 * @returns 格式化后的手机号
 */
const formatPhone = (phone: string): string => {
  if (!phone || phone.length !== 11) {
    return phone;
  }
  return `${phone.slice(0, 3)}****${phone.slice(-4)}`;
};

/**
 * 检查子数组是否完全包含在父数组中
 * @param parentArr 父数组
 * @param childArr 子数组
 * @returns 如果子数组完全包含在父数组中，则返回 true；否则返回 false
 */
const isContain = (parentArr, childArr) => {
  // childArr 为空数组时，默认视为“包含”（可根据需求修改）
  if (childArr.length === 0) return true;
  // 遍历 childArr 所有元素，检查是否都在 parentArr 中
  return childArr.every((item) => parentArr.includes(item));
};

/**
 * 获取OSS图片缩略图
 * @param url 图片URL
 * @param width 宽度
 * @param height 高度
 * @returns 缩略图URL
 */
const getOssThumbnail = (url: string, width?: number, height?: number) => {
  if (!url) return '';
  // 判断是否是OSS链接 (aliyuncs.com)
  if (url.includes('aliyuncs.com')) {
    // 如果已经是缩略图或者是处理过的链接，直接返回
    if (url.includes('x-oss-process')) return url;

    const process = ['image/resize'];
    if (width) process.push(`w_${width}`);
    if (height) process.push(`h_${height}`);
    // 默认使用 m_lfit (等比缩放，限制在矩形内)
    process.push('m_lfit');
    // 增加 webp 格式转换和质量压缩，进一步减小体积
    process.push('format,webp');
    process.push('quality,q_80');

    return `${url}?x-oss-process=${process.join(',')}`;
  }
  return url;
};

/**
 * 设置 cookie
 * @param name cookie 名称
 * @param value cookie 值
 * @param days 过期天数，默认 7 天
 */
const setCookie = (name: string, value: string, days: number = 7) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `; expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value || ''}${expires}; path=/`;
};

/**
 * 获取 cookie
 * @param name cookie 名称
 * @returns cookie 值
 */
const getCookie = (name: string) => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * 格式化时间，返回相对时间或原始时间
 * @param {string|Date} inputTime - 输入的时间（支持字符串、Date对象）
 * @returns {string} 格式化后的时间（X小时前 / X天前 / 原始时间）
 */
const formatRelativeTime = (inputTime: string | Date | number) => {
  if (!inputTime) return '';

  const target = dayjs(inputTime);
  const now = dayjs();

  if (!target.isValid()) {
    return String(inputTime);
  }

  const diffSeconds = now.diff(target, 'second');
  const diffMinutes = now.diff(target, 'minute');
  const diffHours = now.diff(target, 'hour');
  const isSameYear = now.isSame(target, 'year');
  const isYesterday = now.subtract(1, 'day').isSame(target, 'day');
  const isToday = now.isSame(target, 'day');

  if (diffSeconds < 60) {
    return '刚刚';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}分钟前`;
  } else if (isToday) {
    return `${diffHours}小时前`;
  } else if (isYesterday) {
    return `昨天 ${target.format('HH:mm')}`;
  } else if (isSameYear) {
    return target.format('MM-DD');
  } else {
    return target.format('YYYY-MM-DD');
  }
};

export default {
  getBase64,
  getTabsByPage,
  formatNumber,
  getColorByStr,
  copyText,
  downloadImages,
  downloadVideos,
  base64ToFile, // 添加新函数
  formatPhone, // 添加新函数
  isContain, // 添加新函数
  downloadFile, // 添加新函数
  getOssThumbnail, // 添加新函数
  setCookie, // 添加新函数
  getCookie, // 添加新函数
  formatRelativeTime, // 添加新函数
};
